import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Area, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  DollarSign, Building2, Users, LifeBuoy, AlertTriangle, HeartPulse, Radio,
  Boxes, Filter, Globe, Database, RefreshCw, ChevronRight, ShieldAlert, Bot,
} from "lucide-react";

const ORANGE = "#FF5F15";

function fmtMoney(n) { return `NZ$${Number(n || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2 })}`; }
function ago(iso) {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
function bytes(n) {
  if (!n) return "0 B";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function Panel({ title, icon: Icon, children, className = "", testId, right }) {
  return (
    <div className={`bg-[#131316] border border-zinc-800 rounded-sm p-4 ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5 text-[#FF5F15]" />}
          <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">{title}</span>
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, accent, testId }) {
  return (
    <div className={`border rounded-sm p-4 ${accent ? "bg-[#FF5F15] border-[#FF5F15]" : "bg-[#131316] border-zinc-800"}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className={`text-[10px] font-bold uppercase tracking-[0.15em] ${accent ? "text-black/70" : "text-zinc-500"}`}>{label}</span>
        <Icon className={`w-4 h-4 ${accent ? "text-black/60" : "text-[#FF5F15]"}`} />
      </div>
      <div className={`font-display font-black text-3xl mt-2 ${accent ? "text-black" : "text-white"}`}>{value}</div>
      {sub && <div className={`text-[10px] font-mono uppercase tracking-widest mt-1 ${accent ? "text-black/60" : "text-zinc-500"}`}>{sub}</div>}
    </div>
  );
}

const SEV = {
  red: "bg-red-500", amber: "bg-amber-400", blue: "bg-sky-400",
};

function AttentionFeed({ items, onNavigate }) {
  if (!items?.length) {
    return <div className="text-sm text-zinc-500 py-6 text-center font-mono" data-testid="attention-empty">Nothing needs attention. All clear.</div>;
  }
  return (
    <div className="space-y-1.5">
      {items.map((it, i) => (
        <button key={i} onClick={() => onNavigate?.(it.tab)}
                className="w-full flex items-center gap-3 text-left px-3 py-2.5 bg-[#0B0B0D] border border-zinc-800 hover:border-zinc-600 rounded-sm group transition-colors"
                data-testid={`attention-item-${i}`}>
          <span className={`w-2 h-2 rounded-full shrink-0 ${SEV[it.severity] || SEV.blue}`} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-zinc-100 truncate">{it.title}</div>
            <div className="text-[10px] font-mono text-zinc-500 truncate">{it.detail}</div>
          </div>
          <ChevronRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-[#FF5F15] shrink-0" />
        </button>
      ))}
    </div>
  );
}

function GrowthChart({ series }) {
  return (
    <div className="h-56 w-full min-w-0" data-testid="growth-chart">
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
        <ComposedChart data={series} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
          <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 9, fontFamily: "monospace" }}
                 tickFormatter={(d) => d.slice(8, 10) + "/" + d.slice(5, 7)} interval={4} axisLine={{ stroke: "#3f3f46" }} tickLine={false} />
          <YAxis tick={{ fill: "#71717a", fontSize: 9, fontFamily: "monospace" }} allowDecimals={false} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={{ background: "#0B0B0D", border: "1px solid #3f3f46", borderRadius: 2, fontSize: 11 }}
                   labelStyle={{ color: "#a1a1aa", fontFamily: "monospace", fontSize: 10 }} itemStyle={{ padding: 0 }} />
          <Legend wrapperStyle={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }} iconSize={8} />
          <Bar dataKey="jobs" name="Jobs created" fill="#3f3f46" radius={[2, 2, 0, 0]} />
          <Area dataKey="users" name="New users" stroke="#eab308" fill="#eab30822" strokeWidth={2} type="monotone" />
          <Area dataKey="orgs" name="New orgs" stroke={ORANGE} fill={ORANGE + "33"} strokeWidth={2} type="monotone" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function HealthBar({ h }) {
  const total = h.total || 1;
  const segs = [
    ["active_7d", "Active 7d", "bg-emerald-500", "text-emerald-400"],
    ["active_30d", "Active 30d", "bg-yellow-500", "text-yellow-400"],
    ["quiet", "Quiet 30d+", "bg-red-500", "text-red-400"],
    ["never", "Never logged in", "bg-zinc-600", "text-zinc-400"],
  ];
  return (
    <div>
      <div className="flex h-3 rounded-sm overflow-hidden border border-zinc-800" data-testid="health-bar">
        {segs.map(([k, , cls]) => (h[k] > 0 &&
          <div key={k} className={cls} style={{ width: `${(h[k] / total) * 100}%` }} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-3">
        {segs.map(([k, label, cls, txt]) => (
          <div key={k} className="flex items-center gap-2 text-xs">
            <span className={`w-2 h-2 rounded-full ${cls}`} />
            <span className="text-zinc-400 flex-1">{label}</span>
            <span className={`font-mono font-bold ${txt}`} data-testid={`health-${k}`}>{h[k]}</span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-4 pt-3 border-t border-zinc-800">
        {Object.entries(h.plan_distribution || {}).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1.5 bg-[#0B0B0D] border border-zinc-800 px-2 py-1 rounded-sm">
            <span className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">{k}</span>
            <span className="font-mono text-xs font-bold text-zinc-200">{v}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function SupportCommand({ s, onNavigate }) {
  const cells = [
    ["Open", s.open, s.open > 0 ? "text-[#FF5F15]" : "text-zinc-200"],
    ["Waiting", s.waiting, "text-zinc-200"],
    ["New · 7d", s.new_7d, "text-zinc-200"],
    ["Closed all-time", s.closed, "text-zinc-200"],
  ];
  return (
    <div>
      <div className="grid grid-cols-4 gap-2">
        {cells.map(([k, v, cls]) => (
          <div key={k} className="bg-[#0B0B0D] border border-zinc-800 rounded-sm p-2.5 text-center">
            <div className={`font-display font-black text-xl ${cls}`}>{v ?? "—"}</div>
            <div className="text-[8px] font-bold uppercase tracking-widest text-zinc-500 mt-0.5">{k}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 text-xs">
        <span className="inline-flex items-center gap-1.5 text-zinc-400">
          <Bot className="w-3.5 h-3.5 text-emerald-400" /> AI auto-replied <b className="text-emerald-400 font-mono">{s.auto_replied_7d}</b> · 7d
        </span>
        {s.human_required > 0 && (
          <span className="inline-flex items-center gap-1.5 text-red-400 font-bold">
            <ShieldAlert className="w-3.5 h-3.5" /> {s.human_required} need a human
          </span>
        )}
      </div>
      {s.oldest_open && (
        <button onClick={() => onNavigate?.("support")}
                className="w-full mt-3 text-left bg-[#0B0B0D] border border-zinc-800 hover:border-[#FF5F15]/60 rounded-sm p-3 transition-colors"
                data-testid="oldest-open-ticket">
          <div className="text-[9px] font-bold uppercase tracking-widest text-red-400">Oldest open ticket {s.oldest_open.age_hours != null && `· ${s.oldest_open.age_hours >= 24 ? Math.floor(s.oldest_open.age_hours / 24) + "d" : s.oldest_open.age_hours + "h"} old`}</div>
          <div className="text-sm font-bold text-zinc-100 truncate mt-0.5">{s.oldest_open.subject || "(no subject)"}</div>
          <div className="text-[10px] font-mono text-zinc-500">{s.oldest_open.org || "—"} · last reply by {s.oldest_open.last_by || "—"}</div>
        </button>
      )}
    </div>
  );
}

function ActivityFeed({ items }) {
  if (!items?.length) return <div className="text-sm text-zinc-500 py-6 text-center font-mono">No recent activity.</div>;
  return (
    <div className="space-y-0.5 max-h-72 overflow-y-auto pr-1" data-testid="activity-feed">
      {items.map((a, i) => (
        <div key={i} className="flex items-start gap-2.5 py-1.5 border-b border-zinc-800/60 last:border-0">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF5F15] mt-1.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-xs text-zinc-200 truncate">{a.summary || a.type}</div>
            <div className="text-[9px] font-mono text-zinc-500">{a.actor_name || "system"} · {a.org_name} · {ago(a.created_at)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function UsageBars({ usage }) {
  const max = Math.max(1, ...usage.map((u) => u.count_30d));
  return (
    <div className="space-y-2" data-testid="usage-bars">
      {usage.map((u) => (
        <div key={u.module} className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 w-32 shrink-0 truncate">{u.module}</span>
          <div className="flex-1 h-3.5 bg-[#0B0B0D] border border-zinc-800 rounded-sm overflow-hidden">
            <div className="h-full bg-[#FF5F15]/80" style={{ width: `${(u.count_30d / max) * 100}%` }} />
          </div>
          <span className="font-mono text-xs text-zinc-200 w-12 text-right shrink-0">{u.count_30d}</span>
          <span className="font-mono text-[9px] text-zinc-500 w-14 shrink-0">{u.orgs_30d} org{u.orgs_30d === 1 ? "" : "s"}</span>
        </div>
      ))}
    </div>
  );
}

function Funnel({ f }) {
  const steps = [
    ["Signed up", f.signed_up], ["Approved", f.approved], ["Activated (logged in)", f.activated],
    ["Engaged · 14d", f.engaged_14d], ["Paying", f.paying],
  ];
  const top = steps[0][1] || 1;
  return (
    <div className="space-y-2" data-testid="funnel">
      {steps.map(([label, v], i) => (
        <div key={label} className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 w-36 shrink-0">{label}</span>
          <div className="flex-1 h-5 bg-[#0B0B0D] border border-zinc-800 rounded-sm overflow-hidden">
            <div className={`h-full ${i === steps.length - 1 ? "bg-emerald-500/80" : "bg-[#FF5F15]/70"}`}
                 style={{ width: `${(v / top) * 100}%` }} />
          </div>
          <span className="font-mono text-xs text-zinc-200 w-8 text-right shrink-0">{v}</span>
          <span className="font-mono text-[9px] text-zinc-500 w-10 shrink-0">{Math.round((v / top) * 100)}%</span>
        </div>
      ))}
    </div>
  );
}

function Marketing({ m }) {
  const ch = m.civilhub || {};
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <div className="flex gap-2">
          {[["Page views", m.page_views_30d], ["Sessions", m.sessions_30d]].map(([k, v]) => (
            <div key={k} className="flex-1 bg-[#0B0B0D] border border-zinc-800 rounded-sm p-2.5">
              <div className="font-display font-black text-xl text-white">{(v ?? 0).toLocaleString()}</div>
              <div className="text-[8px] font-bold uppercase tracking-widest text-zinc-500">{k} · 30d</div>
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-1">
          {(m.top_pages || []).slice(0, 5).map((p) => (
            <div key={p.path} className="flex items-center justify-between text-[10px] font-mono">
              <span className="text-zinc-400 truncate mr-2">{p.path}</span>
              <span className="text-zinc-200 font-bold">{p.n}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Civil Hub · SEO · 30d</div>
        <div className="space-y-1.5">
          {[["Calculator starts", ch.calc_starts], ["Searches", ch.searches], ["CTA clicks", ch.cta_clicks], ["Guide views", ch.guide_views], ["Shares", ch.shares]].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-xs">
              <span className="text-zinc-400">{k}</span>
              <span className="font-mono font-bold text-zinc-200">{v ?? "—"}</span>
            </div>
          ))}
        </div>
        {(m.top_countries || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {m.top_countries.map((c) => (
              <span key={c.country} className="text-[9px] font-mono bg-[#0B0B0D] border border-zinc-800 px-1.5 py-0.5 rounded-sm text-zinc-400">
                {c.country} · {c.n}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SystemStrip({ s }) {
  const st = { ok: ["HEALTHY", "text-emerald-400", "bg-emerald-500"], warning: ["UPGRADE SOON", "text-amber-400", "bg-amber-500"], critical: ["UPGRADE NOW", "text-red-400", "bg-red-500"] }[s.status] || ["UNKNOWN", "text-zinc-400", "bg-zinc-600"];
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className={`text-xs font-black uppercase tracking-wider ${st[1]}`} data-testid="system-status">DB {st[0]}</span>
        <span className="font-mono text-xs text-zinc-400">{s.used_pct != null ? `${s.used_pct}% of ${s.capacity_mb} MB` : "—"}</span>
      </div>
      <div className="mt-2 h-2.5 bg-[#0B0B0D] border border-zinc-800 rounded-sm overflow-hidden">
        <div className={`h-full ${st[2]}`} style={{ width: `${Math.min(100, s.used_pct || 0)}%` }} />
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        {[["Documents", (s.objects || 0).toLocaleString()], ["Data size", bytes(s.data_size)]].map(([k, v]) => (
          <div key={k} className="bg-[#0B0B0D] border border-zinc-800 rounded-sm p-2">
            <div className="font-mono text-sm font-bold text-zinc-200">{v}</div>
            <div className="text-[8px] font-bold uppercase tracking-widest text-zinc-500">{k}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 space-y-1.5">
        <div className={`flex items-center justify-between text-xs ${s.crashes_7d ? "text-amber-400" : "text-zinc-500"}`}>
          <span className="inline-flex items-center gap-1.5"><AlertTriangle className="w-3 h-3" /> Client crashes · 7d</span>
          <span className="font-mono font-bold" data-testid="crashes-7d">{s.crashes_7d}</span>
        </div>
        <div className={`flex items-center justify-between text-xs ${s.failed_logins_7d > 20 ? "text-amber-400" : "text-zinc-500"}`}>
          <span className="inline-flex items-center gap-1.5"><ShieldAlert className="w-3 h-3" /> Failed logins · 7d</span>
          <span className="font-mono font-bold" data-testid="failed-logins-7d">{s.failed_logins_7d}</span>
        </div>
      </div>
    </div>
  );
}

export default function CommandCentre({ onNavigate, usageOnly = false }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/dashboard");
      setD(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!d) {
    return (
      <div className="bg-[#0A0A0C] border-2 border-zinc-800 rounded-md p-10 text-center font-mono text-sm text-zinc-500" data-testid="command-centre-loading">
        {loading ? "Loading command centre…" : "Failed to load."}
      </div>
    );
  }

  if (usageOnly) return <div className="space-y-6"><section className="pa-panel"><h2>Product usage · last 30 days</h2><p className="platform-muted mb-5">Canonical record counts by module.</p><div className="pa-table-wrap"><table className="pa-table"><thead><tr><th>Module</th><th>Records</th><th>Organisations</th></tr></thead><tbody>{d.usage.map(u => <tr key={u.module}><td>{u.module}</td><td>{u.count_30d}</td><td>{u.orgs_30d}</td></tr>)}</tbody></table></div></section><section className="pa-panel"><h2>Recent platform activity</h2>{d.activity.map((a, i) => <div className="py-3 border-b" key={i}><p>{a.summary}</p><small className="platform-muted">{a.org_name} · {a.actor_name} · {ago(a.created_at)}</small></div>)}</section><details className="pa-panel"><summary>Business growth · 30 days</summary><GrowthChart series={d.growth.series} /></details></div>;
  const k = d.kpis;
  return (
    <div className="bg-[#0A0A0C] border-2 border-zinc-800 rounded-md p-4 lg:p-5 space-y-4" data-testid="command-centre">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Command Centre · Live</span>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15] transition-colors" data-testid="command-centre-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="MRR" value={fmtMoney(k.mrr_nzd)} sub={`${k.paying_orgs} paying org${k.paying_orgs === 1 ? "" : "s"}`} icon={DollarSign} accent testId="kpi-mrr" />
        <Kpi label="Organisations" value={k.total_orgs} sub={`+${k.new_orgs_30d} · 30d`} icon={Building2} testId="kpi-orgs" />
        <Kpi label="Users" value={k.total_users} sub={`+${k.new_users_30d} · 30d`} icon={Users} testId="kpi-users" />
        <Kpi label="Open support" value={k.open_tickets} sub={`${k.tickets_new_7d} new · 7d`} icon={LifeBuoy} testId="kpi-support" />
      </div>

      {/* Row 1: Growth + Attention */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Business growth · 30 days" icon={Building2} className="lg:col-span-2" testId="panel-growth">
          <GrowthChart series={d.growth.series} />
        </Panel>
        <Panel title="Needs attention" icon={AlertTriangle} testId="panel-attention">
          <AttentionFeed items={d.attention} onNavigate={onNavigate} />
        </Panel>
      </div>

      {/* Row 2: Customer health + Support command */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Customer health" icon={HeartPulse} testId="panel-health"
               right={<span className="font-mono text-[10px] text-zinc-500">{d.customer_health.total} orgs</span>}>
          <HealthBar h={d.customer_health} />
        </Panel>
        <Panel title="Support command" icon={LifeBuoy} testId="panel-support">
          <SupportCommand s={d.support} onNavigate={onNavigate} />
        </Panel>
      </div>

      {/* Row 3: Live activity + Product usage */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Live platform activity" icon={Radio} testId="panel-activity">
          <ActivityFeed items={d.activity} />
        </Panel>
        <Panel title="Product usage · 30 days" icon={Boxes} testId="panel-usage">
          <UsageBars usage={d.usage} />
        </Panel>
      </div>

      {/* Row 4: Funnel + Marketing + System */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Growth funnel" icon={Filter} testId="panel-funnel">
          <Funnel f={d.funnel} />
        </Panel>
        <Panel title="Marketing & SEO" icon={Globe} testId="panel-marketing">
          <Marketing m={d.marketing} />
        </Panel>
        <Panel title="System health" icon={Database} testId="panel-system">
          <SystemStrip s={d.system} />
        </Panel>
      </div>

      <div className="text-right text-[9px] font-mono text-zinc-600">generated {ago(d.generated_at)}</div>
    </div>
  );
}
